import { useCallback, useEffect, useState } from "react";
import { FiCheckCircle } from "react-icons/fi";
import Loading from "../../Utils/Loading/Loading";
import Backend from "../../Backend/Backend";
import toast from "react-hot-toast";

const backend = await new Backend().init();

const S = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

  .rl-overlay {
    position: fixed; inset: 0; z-index: 50;
    background: rgba(0,0,0,.5);
    display: flex; align-items: center; justify-content: center;
    padding: 16px; font-family: 'Inter', sans-serif; box-sizing: border-box;
  }

  .rl-box {
    background: var(--background);
    border: 1px solid var(--hover);
    border-radius: 16px;
    box-shadow: 0 8px 32px rgba(0,0,0,.2);
    width: 100%; max-width: 500px;
    max-height: 88vh;
    display: flex; flex-direction: column;
    overflow: hidden;
    animation: rlIn .2s ease;
  }

  @keyframes rlIn {
    from { opacity: 0; transform: translateY(10px) scale(.98); }
    to   { opacity: 1; transform: translateY(0) scale(1); }
  }

  .rl-head {
    padding: 16px 20px 14px;
    border-bottom: 1px solid var(--hover);
    flex-shrink: 0;
  }
  .rl-head h2 { margin: 0; font-size: 15px; font-weight: 600; color: var(--text); }
  .rl-head p  { margin: 2px 0 0; font-size: 11px; color: var(--subtext); opacity: .7; }

  .rl-body {
    flex: 1; overflow-y: auto; padding: 14px 20px;
    scrollbar-width: thin; scrollbar-color: var(--hover) transparent;
  }
  .rl-body::-webkit-scrollbar { width: 4px; }
  .rl-body::-webkit-scrollbar-thumb { background: var(--hover); border-radius: 4px; }

  .rl-item {
    display: flex; align-items: flex-start; gap: 10px;
    padding: 10px 12px;
    border: 1px solid var(--hover);
    border-radius: 10px; margin-bottom: 8px;
    background: var(--background);
    transition: border-color .15s;
  }
  .rl-item:last-child { margin-bottom: 0; }
  .rl-item:hover { border-color: var(--primary); }

  .rl-icon { color: var(--primary); flex-shrink: 0; margin-top: 1px; font-size: 14px; }

  .rl-item p { margin: 0; font-size: 12px; line-height: 1.55; color: var(--text); }

  .rl-empty { text-align: center; font-size: 13px; color: var(--subtext); opacity: .6; padding: 24px 0; }

  .rl-foot {
    border-top: 1px solid var(--hover);
    padding: 14px 20px;
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; flex-shrink: 0;
    background: var(--background);
  }

  .rl-check {
    display: flex; align-items: center; gap: 8px;
    cursor: pointer; user-select: none;
  }
  .rl-check input[type="checkbox"] {
    accent-color: var(--buy);
    width: 15px; height: 15px; cursor: pointer;
  }
  .rl-check span { font-size: 12px; color: var(--text); }

  .rl-btn {
    height: 38px; padding: 0 20px; border: none; border-radius: 10px;
    background: var(--primary); color: #fff;
    font-size: 13px; font-weight: 600; cursor: pointer;
    transition: opacity .18s; white-space: nowrap;
    font-family: 'Inter', sans-serif;
  }
  .rl-btn:hover:not(:disabled) { opacity: .85; }
  .rl-btn:disabled { opacity: .25; cursor: not-allowed; }

  @media (max-width: 480px) {
    .rl-foot { flex-direction: column; align-items: stretch; }
    .rl-btn { width: 100%; height: 42px; }
  }
`;

export default function Rules({ onAccept }) {
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rules, setRules] = useState([]);

  if (!document.getElementById("rl-style")) {
    const s = document.createElement("style"); s.id = "rl-style"; s.textContent = S;
    document.head.appendChild(s);
  }

  const loadRules = useCallback(async () => {
    setLoading(true);
    try {
      const r = await backend.rules({
        server: localStorage.getItem("server"),
        id: localStorage.getItem("id"),
        username: localStorage.getItem("username"),
        token: localStorage.getItem("token"),
      });
      if (r.error === "False") setRules(r.rules || []);
      else { toast.error(r.message); setRules([]); }
    } catch {
      toast.error("Failed to fetch rules."); setRules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadRules(); }, [loadRules]);

  return (
    <div className="rl-overlay">
      <div className="rl-box">

        <div className="rl-head">
          <h2>📜 Rules & Regulations</h2>
          <p>Please read carefully before continuing</p>
        </div>

        <div className="rl-body">
          {loading ? (
            <Loading height={200} />
          ) : rules.length > 0 ? (
            rules.map((rule) => (
              <div className="rl-item" key={rule.id}>
                <FiCheckCircle className="rl-icon" />
                <p>{rule.msg}</p>
              </div>
            ))
          ) : (
            <p className="rl-empty">No rules found.</p>
          )}
        </div>

        <div className="rl-foot">
          <label className="rl-check">
            <input type="checkbox" checked={agree} onChange={() => setAgree(!agree)} />
            <span>I have read and agree to the rules</span>
          </label>
          <button className="rl-btn" disabled={!agree} onClick={onAccept}>
            Accept & Continue
          </button>
        </div>

      </div>
    </div>
  );
}
